import { Address, AutocompleteOption, Client } from "../shared/types";

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
} from "@/components/ui/combobox";
import {
  comboboxChangeEvent,
  formComboboxInputClassName,
} from "@/components/forms/FormCombobox";
import {
  scrollVirtualizedHighlight,
  VirtualizedComboboxList,
} from "@/components/forms/VirtualizedComboboxList";
import { useCreatableComboboxFilter } from "@/components/forms/useCreatableComboboxFilter";
import { getCountryFlagURLFromCountryOrString } from "../utils/countries";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "cn";
import type { Virtualizer } from "@tanstack/react-virtual";

interface ClientSelectProps {
  value?: Client;
  onChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: string | AutocompleteOption<Client> | null
  ) => unknown;
  clients: Client[];
  disabled?: boolean;
}

function formatClientAddress(address: Address | undefined): string | undefined {
  if (!address) return undefined;

  const locality = [
    address.city,
    [address.state, address.postCode].filter(Boolean).join(" "),
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");

  const formatted = [
    address.addressLine1,
    address.addressLine2,
    locality,
    address.country,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");

  return formatted || undefined;
}

function clientIdsLine(client: Client): string | undefined {
  const parts = [
    client.airtableId ? `Airtable ${client.airtableId}` : undefined,
    client.id ? `Platform ${client.id}` : undefined,
  ].filter(Boolean);

  return parts.length ? parts.join(" · ") : undefined;
}

/** Search haystack so typing matches name, IDs, and address. */
function clientFilterLabel(client: Client): string {
  return [
    client.name,
    client.airtableId,
    client.id,
    formatClientAddress(client.address),
  ]
    .filter(Boolean)
    .join(" ");
}

/** Input text: client name, or the creatable "Add New Client" label. */
function optionDisplayLabel(option: AutocompleteOption<Client>) {
  if (option.inputValue) return option.label;
  return option.val?.name || option.label;
}

/** Name used for equality / parent `inputValue` (creatable stores Foo here). */
function optionValueName(option: AutocompleteOption<Client>) {
  return option.inputValue ?? option.val?.name ?? option.label;
}

/**
 * Client picker with inline "Add New Client" creatable row.
 *
 * Close policy (same as StringFormCombobox / CampaignSelect / clearOnBlur):
 * blur / outside / Escape reverts the input to the last committed client.
 * New clients are committed only by selecting the creatable row (or Enter while
 * it is highlighted) — not by leaving leftover filter text.
 *
 * Creatable display: show `Add New Client: "Foo"` (option.label), while
 * `inputValue` on the option remains `"Foo"` for parents — same as old MUI.
 */
function ClientSelect(props: ClientSelectProps) {
  const { clients, disabled, onChange, value } = props;
  const [open, setOpen] = useState(false);
  const virtualizerRef = useRef<Virtualizer<HTMLDivElement, Element> | null>(
    null
  );

  const clientOptions: AutocompleteOption<Client>[] = useMemo(
    () =>
      clients.map((client) => ({
        val: client,
        label: clientFilterLabel(client),
      })),
    [clients]
  );

  const selectedFromProps = useMemo<AutocompleteOption<Client> | null>(
    () =>
      value ? { val: value, label: value.name || value.id! } : null,
    [value]
  );

  // Local value so a creatable row can stay selected until props.value catches up.
  // Controlled `value={selectedFromProps}` alone made Enter on "Add New" clear:
  // Base UI committed the creatable option, then props still had the old/empty
  // Client and the clearOnBlur ref was reset from props on the next render.
  const [comboboxValue, setComboboxValue] =
    useState<AutocompleteOption<Client> | null>(selectedFromProps);

  const committedLabelRef = useRef(
    selectedFromProps ? optionDisplayLabel(selectedFromProps) : ""
  );
  const [inputValue, setInputValue] = useState(committedLabelRef.current);

  // Sync from parent Client only when props change — not on open/close
  // (closing after "Add New" must keep the creatable selection until props catch up).
  useEffect(() => {
    setComboboxValue(selectedFromProps);
    const label = selectedFromProps
      ? optionDisplayLabel(selectedFromProps)
      : "";
    committedLabelRef.current = label;
    if (!open) setInputValue(label);
    // intentionally omit `open`: clearOnBlur uses committedLabelRef on close
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [selectedFromProps]);

  const createOption = useCallback(
    (typed: string): AutocompleteOption<Client> => ({
      val: undefined,
      inputValue: typed,
      label: `Add New Client: "${typed}"`,
    }),
    []
  );

  // Filter from the typed query; when showing a committed creatable label, use
  // inputValue on the option so we don't filter on `Add New Client: "…"`.
  const filterQuery =
    comboboxValue?.inputValue && inputValue === comboboxValue.label
      ? comboboxValue.inputValue
      : inputValue;

  const { filteredItems } = useCreatableComboboxFilter(
    clientOptions,
    filterQuery,
    createOption
  );

  const itemToStringLabel = useCallback(
    (option: AutocompleteOption<Client>) => optionDisplayLabel(option),
    []
  );

  const isItemEqualToValue = useCallback(
    (a: AutocompleteOption<Client>, b: AutocompleteOption<Client>) => {
      // Treat creatable `{ inputValue }` and `{ val: { name } }` as the same client.
      if (a.inputValue || b.inputValue || !a.val?.id || !b.val?.id) {
        return optionValueName(a) === optionValueName(b);
      }
      return a.val.id === b.val.id;
    },
    []
  );

  return (
    <Combobox
      id="client-select"
      virtualized
      items={clientOptions}
      filteredItems={filteredItems}
      filter={null}
      value={comboboxValue}
      inputValue={inputValue}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) return;
        // clearOnBlur: discard filter text; do not invent a client from it.
        setInputValue(committedLabelRef.current);
      }}
      onValueChange={(next) => {
        setComboboxValue(next);
        if (next == null) {
          committedLabelRef.current = "";
          setInputValue("");
          onChange(comboboxChangeEvent(), null);
          return;
        }
        // Display client name or `Add New Client: "Foo"`; parents still
        // read next.inputValue === "Foo".
        const label = optionDisplayLabel(next);
        committedLabelRef.current = label;
        setInputValue(label);
        onChange(comboboxChangeEvent(), next);
      }}
      onInputValueChange={setInputValue}
      onItemHighlighted={(_item, { reason, index }) => {
        scrollVirtualizedHighlight(virtualizerRef.current, index, reason);
      }}
      disabled={disabled}
      required
      autoHighlight
      autoComplete="off"
      itemToStringLabel={itemToStringLabel}
      isItemEqualToValue={isItemEqualToValue}
    >
      <ComboboxInput
        disabled={disabled}
        showClear={!disabled}
        className={cn(formComboboxInputClassName)}
      />
      <ComboboxContent>
        <ComboboxEmpty>No clients found.</ComboboxEmpty>
        <VirtualizedComboboxList<AutocompleteOption<Client>>
          open={open}
          estimateSize={72}
          itemClassName="items-start"
          virtualizerRef={virtualizerRef}
          getItemKey={(option) => option.val?.id ?? option.label}
        >
          {(option) => {
            if (option.inputValue || !option.val) {
              return option.label;
            }

            const client = option.val;
            const address = formatClientAddress(client.address);
            const idsLine = clientIdsLine(client);
            const countryFlagURL = getCountryFlagURLFromCountryOrString(
              client.address?.country
            );

            return (
              <div className="min-w-0 space-y-0.5">
                {/* Flag + name: one row so the flag stays visible if the name truncates. */}
                <div className="flex min-w-0 items-center">
                  {countryFlagURL ? (
                    <img
                      src={countryFlagURL}
                      alt=""
                      className="mr-1 h-3 w-4 shrink-0"
                      loading="lazy"
                    />
                  ) : null}
                  <strong className="truncate">{client.name}</strong>
                </div>
                {address ? (
                  <div className="truncate text-xs/4 text-muted-foreground">
                    {address}
                  </div>
                ) : null}
                {idsLine ? (
                  <div className="truncate text-tiny/4 text-muted-foreground">
                    {idsLine}
                  </div>
                ) : null}
              </div>
            );
          }}
        </VirtualizedComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

export default ClientSelect;
